import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// sai's Flipbook (Smart Reports): a real page turn, not a carousel. The page
// being left sits on a leaf with a printed front and a blank back, and the leaf
// swings about its left edge to reveal the page under it. Pages are React
// nodes, so scans and designed cards turn the same way.
//   pages   — array of nodes
//   label   — what the book is, for the page counter
//   ids     — { prev, next } data-trapezium-ids for the page buttons
//   caption — optional: (page index) -> the counter's words
//   onPage  — optional: called with the page index once a turn lands
//   about   — optional: what each page shows, in words. The book itself is
//             decorative (aria-hidden), so this is how the assistant and screen
//             readers learn what is on the page; it is read with the counter.
// Under reduced motion the page changes at once, with no turn.
const TURN_MS = 820;

export default function Flipbook({ pages, label, ids, caption, onPage, about }) {
  const [at, setAt] = useState(0);
  const [turn, setTurn] = useState(null); // { to, dir: 1 | -1 }
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const go = (dir) => {
    const to = at + dir;
    if (turn || to < 0 || to >= pages.length) return;
    const still =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const land = () => {
      setAt(to);
      setTurn(null);
      if (onPage) onPage(to);
    };
    if (still) {
      land();
      return;
    }
    setTurn({ to, dir });
    timer.current = setTimeout(land, TURN_MS);
  };

  // While turning forward the leaf carries the current page away over the
  // next one; turning back it brings the previous page in over the current.
  const under = turn ? (turn.dir > 0 ? pages[turn.to] : pages[at]) : pages[at];
  const leaf = turn ? (turn.dir > 0 ? pages[at] : pages[turn.to]) : pages[at];

  return (
    <div className="flip">
      <div className="flip-book" data-turning={turn ? (turn.dir > 0 ? 'fwd' : 'back') : undefined} aria-hidden="true">
        <div className="flip-layer">{under}</div>
        <div className="flip-shade" />
        <div className="flip-leaf">
          <div className="flip-face flip-front">{leaf}</div>
          <div className="flip-face flip-back" />
        </div>
      </div>
      <div className="flip-nav">
        <button
          type="button"
          onClick={() => go(-1)}
          disabled={at === 0}
          aria-label="Previous page"
          data-trapezium-id={ids.prev}
        >
          <ChevronLeft size={18} aria-hidden="true" />
        </button>
        <span aria-live="polite">
          {caption ? caption(at) : `${label}: page ${at + 1} of ${pages.length}`}
          {about && about[at] ? <span className="sr-only">. {about[at]}</span> : null}
        </span>
        <button
          type="button"
          onClick={() => go(1)}
          disabled={at === pages.length - 1}
          aria-label="Next page"
          data-trapezium-id={ids.next}
        >
          <ChevronRight size={18} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
